import type { Katalog, OrderInput, PaymentInput } from '@santap/shared';

const BASIS = import.meta.env.VITE_API ?? '/api';

export class GalatApi extends Error {
  constructor(
    readonly kode: string,
    pesan: string,
    readonly status: number,
    readonly detail?: unknown,
  ) {
    super(pesan);
  }
}

/** Galat jaringan — berbeda dari galat server, dan ditangani berbeda pula. */
export class GalatJaringan extends Error {
  constructor(pesan = 'Tidak bisa menghubungi server') {
    super(pesan);
  }
}

let token: string | null = null;
export const setToken = (t: string | null) => { token = t; };
export const adaToken = () => token !== null;

async function minta<T>(
  metode: string,
  jalur: string,
  body?: unknown,
  header: Record<string, string> = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASIS + jalur, {
      method: metode,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...header,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // Jaringan putus. Ini BUKAN kegagalan — kasir memang dirancang untuk
    // tetap bekerja, dan pemanggil yang menentukan apa yang terjadi selanjutnya.
    throw new GalatJaringan();
  }

  const teks = await res.text();
  let json: any = null;
  try { json = teks ? JSON.parse(teks) : null; } catch {}

  if (!res.ok) {
    throw new GalatApi(
      json?.kode ?? 'galat',
      json?.pesan ?? `Server membalas ${res.status}`,
      res.status,
      json?.detail,
    );
  }
  return json as T;
}

export const api = {
  masuk: (email: string, sandi: string) =>
    minta<{ token: string; pengguna: any; tenant: any; outlets: any[] }>(
      'POST', '/auth/masuk', { email, sandi },
    ),

  masukPin: (terminalId: string, pin: string) =>
    minta<{ token: string; pengguna: any; tenant: any; outlets: any[] }>(
      'POST', '/auth/masuk-pin', { terminalId, pin },
    ),

  katalog: (outletId: string) => minta<Katalog>('GET', `/katalog/${outletId}`),

  bukaShift: (outletId: string, terminalId: string, kasAwal: number) =>
    minta<any>('POST', '/shift/buka', { outletId, terminalId, kasAwal }),

  rekapShift: (id: string) => minta<any>('GET', `/shift/${id}/rekap`),

  tutupShift: (id: string, kasHitung: number, catatan?: string) =>
    minta<any>('POST', `/shift/${id}/tutup`, { kasHitung, catatan }),

  kirimPesanan: (p: OrderInput) =>
    minta<any>('POST', '/pesanan', p, { 'Idempotency-Key': p.id }),

  sinkronPesanan: (pesanan: OrderInput[]) =>
    minta<{ diterima: number; gagal: number; hasil: any[] }>(
      'POST', '/pesanan/sinkron', { pesanan },
    ),

  /** Tagihan belum lunas dari semua kanal — kiosk, QR meja, online. */
  tagihanTerbuka: (outletId: string) =>
    minta<{
      id: string; nomorStruk: string; nomorAntrean: number | null;
      channel: string; tipe: string; meja: string | null; namaPelanggan: string | null;
      total: number; terbayar: number; sisa: number; jumlahItem: number;
      tertahan: boolean; dibuatPada: string;
    }[]>('GET', `/pesanan/terbuka/${outletId}`),

  rincianTagihan: (orderId: string) =>
    minta<any>('GET', `/pesanan/${orderId}/rincian`),

  bayar: (p: PaymentInput) =>
    minta<any>('POST', '/pembayaran', p, { 'Idempotency-Key': p.id }),

  buatQris: (orderId: string) =>
    minta<{
      paymentId: string; referenceId: string; gateway: string; namaGateway: string;
      qrString: string; qrImageUrl?: string; jumlah: number; kedaluwarsaPada?: string;
    }>('POST', `/pembayaran/qris/${orderId}`),

  statusQris: (paymentId: string) =>
    minta<{ status: string }>('GET', `/pembayaran/qris/${paymentId}/status`),
};
